import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, Activity, Workflow, Cpu, Network, Sparkles, ShieldCheck,
  Gauge, GitBranch, Bot, BarChart3, Zap, RefreshCcw,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';
import SelfHealingClip from '../components/SelfHealingClip';

const IMG_OBS  = 'https://images.unsplash.com/photo-1650327034581-1711a15a5430?w=1200&q=80';
const IMG_HEAL = 'https://images.unsplash.com/photo-1603054400223-7c796e60ba9d?w=1200&q=80';
const IMG_AI   = 'https://images.unsplash.com/photo-1617791160536-598cf32026fb?w=1200&q=80';

const Pillar = ({ id, eyebrow, title, lede, image, points, icon: Icon, reverse = false }) => (
  <section id={id} className="relative bg-black scroll-mt-24">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className={`grid lg:grid-cols-12 gap-10 lg:gap-14 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 mb-5">
            <Icon className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">{eyebrow}</span>
          </div>
          <h2 className="text-[40px] sm:text-[52px] lg:text-[60px] leading-[1.02] tracking-[-0.025em] font-semibold text-white">
            {title}
          </h2>
          <p className="mt-5 text-[16px] lg:text-[17px] text-neutral-300 leading-relaxed max-w-xl">{lede}</p>
          <ul className="mt-7 space-y-4 max-w-xl">
            {points.map((p) => (
              <li key={p.title} className="flex gap-3.5">
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-neutral-950 border border-neutral-800 flex items-center justify-center">
                  <p.icon className="w-4 h-4 text-blue-400" />
                </span>
                <div>
                  <h4 className="text-[15.5px] font-semibold text-white">{p.title}</h4>
                  <p className="mt-1 text-[13.5px] text-neutral-400 leading-relaxed">{p.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden border border-neutral-800 aspect-[4/3] bg-neutral-950 group">
            <img src={image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/30 to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-2xl pointer-events-none" />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[11px] text-blue-300">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" /> Live
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Overview = () => {
  const { open: openLead } = useLeadDialog();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 20%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Platform</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">Overview</span>
            </nav>
            <div className="mt-7 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-7">
                <p className="text-[13px] uppercase tracking-[0.18em] text-blue-400">The Platform · Overview</p>
                <h1 className="mt-3 text-[38px] sm:text-[52px] md:text-[64px] lg:text-[80px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                  See it. Heal it. <br />
                  <span className="italic font-light brand-gradient-text">Automate it.</span>
                </h1>
                <p className="mt-6 text-[17px] lg:text-[18px] text-neutral-300 leading-relaxed max-w-2xl">
                  The MetaxuOne Platform unifies <strong className="text-white font-semibold">observability</strong>, <strong className="text-white font-semibold">self-healing</strong>, and <strong className="text-white font-semibold">AI agents</strong> on top of a single Context Model — so your processes don&rsquo;t just stay visible, they keep getting better on their own.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button onClick={() => openLead({ source: 'overview-cta', title: 'See MetaxuOne in action' })} className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2" data-testid="overview-book-demo-btn">
                    Book a live demo <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href="#observability" className="h-12 px-6 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center" data-testid="overview-explore-pillars-link">
                    Explore the pillars
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <SelfHealingClip />
              </div>
            </div>

            {/* Pillars summary cards */}
            <div className="mt-14 grid sm:grid-cols-3 gap-4 max-w-5xl">
              {[
                { icon: Activity, label: 'Observability', text: 'Real-time visibility into every process & system', href: '#observability' },
                { icon: RefreshCcw, label: 'Self-Healing', text: 'Autonomous remediation before issues escalate', href: '#self-healing' },
                { icon: Cpu, label: 'AI Agents', text: 'Reasoning agents grounded in your operational context', href: '#ai-agents' },
              ].map((p) => (
                <a key={p.label} href={p.href} className="group rounded-2xl border border-neutral-800 bg-neutral-950 p-5 hover:border-blue-500 hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-white text-neutral-950 flex items-center justify-center">
                      <p.icon className="w-4.5 h-4.5" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-500 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <h3 className="mt-4 text-[16px] font-semibold text-white">{p.label}</h3>
                  <p className="mt-1 text-[12.5px] text-neutral-400 leading-relaxed">{p.text}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Pillar: Observability */}
        <Pillar
          id="observability"
          eyebrow="Observability"
          icon={Activity}
          title={<>End-to-end visibility, in <span className="italic font-light text-neutral-400">real time.</span></>}
          lede="See every process, system, and decision as it happens. MetaxuOne ingests data from every layer of your enterprise — ERP, CRM, ITSM, custom apps — and assembles a single, living model you can trust."
          image={IMG_OBS}
          points={[
            { icon: Gauge,      title: 'Live process telemetry',  text: 'Continuous capture of activity, latency, throughput, and exceptions across systems.' },
            { icon: BarChart3,  title: 'KPI command center',      text: 'Trend, alert, and drill from board-level KPIs to the single transaction behind them.' },
            { icon: Network,    title: 'Cross-system lineage',    text: 'Trace any outcome back through every system, agent, and human that touched it.' },
          ]}
        />

        {/* Pillar: Self-Healing */}
        <Pillar
          id="self-healing"
          eyebrow="Self-Healing"
          icon={RefreshCcw}
          title={<>Resilience that <span className="italic font-light text-neutral-400">runs itself.</span></>}
          lede="Don't just spot deviations — close them. The platform pairs detection with policy-driven remediation, so processes correct themselves before they impact the customer or the close."
          image={IMG_HEAL}
          reverse
          points={[
            { icon: ShieldCheck, title: 'Detect deviations in flight', text: 'Statistical and AI models pinpoint drift, bottlenecks, and outliers as they happen.' },
            { icon: Zap,         title: 'Trigger guided remediations', text: 'Run codified playbooks — automations, approvals, or human-in-the-loop tasks.' },
            { icon: GitBranch,   title: 'Closed-loop improvement',     text: 'Every healed event feeds the model so the next deviation is caught faster.' },
          ]}
        />

        {/* Pillar: AI Agents */}
        <Pillar
          id="ai-agents"
          eyebrow="AI Agents"
          icon={Cpu}
          title={<>Agents grounded in <span className="italic font-light text-neutral-400">your operational truth.</span></>}
          lede="Generic AI doesn&rsquo;t know your business. MetaxuOne agents reason on top of the Context Model — your processes, your KPIs, your data — so they make decisions you can audit and stand behind."
          image={IMG_AI}
          points={[
            { icon: Bot,        title: 'Context-aware reasoning', text: 'Agents read the live process graph before they decide or act — no hallucinated state.' },
            { icon: Workflow,   title: 'Workflow orchestration',  text: 'Coordinate humans, agents, and existing systems against the same source of truth.' },
            { icon: Sparkles,   title: 'Continuous learning',     text: 'Outcomes flow back into the model — every decision sharpens the next.' },
          ]}
        />

        {/* Closing CTA */}
        <section className="relative bg-neutral-950 border-t border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(50% 60% at 20% 30%, rgba(59,130,246,0.18), transparent), radial-gradient(50% 60% at 80% 70%, rgba(255,255,255,0.04), transparent)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
            <h2 className="text-[36px] lg:text-[52px] leading-[1.05] tracking-[-0.02em] font-semibold text-white max-w-3xl mx-auto">
              One platform. <span className="italic font-light text-neutral-400">Three superpowers.</span>
            </h2>
            <p className="mt-5 text-[16px] text-neutral-300 max-w-2xl mx-auto">Bring observability, self-healing, and AI agents into the same operating fabric — and let your processes start running themselves.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button onClick={() => openLead({ source: 'overview-bottom-cta', title: 'See MetaxuOne in action' })} className="h-12 px-7 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2">
                Book a live demo <ArrowRight className="w-4 h-4" />
              </Button>
              <Link to="/" className="h-12 px-7 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center">
                Back to home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Overview;
